import { Button } from '@src/components/ui/button/button';
import React, { useEffect, useState } from 'react';
import { cn } from '@src/utils/ui';
import PropTypes from 'prop-types';
import { CriminalRecordCheckSuccessModal } from './success-modal';
import { CriminalRecordCheckProceedModal } from './proceed-modal';
import { ErrorCallout } from '@src/components/common/callouts/error-callout/error-callout';
import { SuccessCallout } from '@src/components/common/callouts/success-callout/success-callout';
import { InfoCallout } from '@src/components/common/callouts/info-callout/info-callout';
import { CriminalCheckBadge } from '@src/components/common/badges/criminal-record-check-badge/criminal-record-check-badge';
import { axios } from '@src/lib/axios';
import toastr from 'toastr';

const useCriminalRecordStatusBadge = props => {
    const [criminalRecordCheck, setCriminalRecordCheck] = useState(props.user?.CriminalRecordCheck || {});

    if (criminalRecordCheck.HasErrors) {
        return (
            <ErrorCallout>
                Error, contact BCAA Customer Support.{' '}
                <a href='/pages/contact-us' target='_blank'>
                    Contact us
                </a>
            </ErrorCallout>
        );
    }

    if (criminalRecordCheck.Retries === 3 && criminalRecordCheck.InviteFailed) {
        return <EmailNotSent {...props} setCriminalRecordCheck={setCriminalRecordCheck} />;
    }

    if (criminalRecordCheck.NotCleared) {
        return (
            <ErrorCallout>
                Your criminal record check is not clear. Please contact Certn for any disputes (
                <a href='mailto:support@certn.co' className='tw-underline'>
                    support@certn.co
                </a>
                ).
            </ErrorCallout>
        );
    }

    if (criminalRecordCheck.Cleared) return <SuccessCallout>Completed. Badge applied to your profile.</SuccessCallout>;

    if (criminalRecordCheck.Completed) {
        return <SuccessCallout>Processing report (may take up to 24 hours)</SuccessCallout>;
    }

    if (criminalRecordCheck.InProgress) return <InfoCallout>Background check in progress.</InfoCallout>;

    if (criminalRecordCheck.InvitedAndPending) {
        return (
            <div className='tw-space-y-[16px]'>
                <InfoCallout>Payment received. Check email for next steps.</InfoCallout>
                <div>
                    <div className='tw-text-[16px] tw-font-[500]'>Didn't receive an email?</div>
                    <p>
                        Please contact our{' '}
                        <a
                            href='https://task.marketplace.bcaa.com/pages/contact-us'
                            target='_blank'
                            className='tw-underline'
                        >
                            customer support team
                        </a>{' '}
                        and we'll help you out.
                    </p>
                </div>
            </div>
        );
    }

    if (criminalRecordCheck.InvitationSent) {
        return <SuccessCallout>Payment received. Email with next steps to be sent shortly.</SuccessCallout>;
    }
};

export const CriminalRecordCheck = props => {
    const [openProceed, setOpenProceed] = useState(false);
    const [hasPaid, setHasPaid] = useState(false);
    const criminalRecordCheckStatus = useCriminalRecordStatusBadge(props);
    const handlePayment = () => {
        setOpenProceed(true);
    };

    useEffect(() => {
        if (typeof window !== 'undefined') {
            const urlParams = new URLSearchParams(window.location.search);
            setOpenProceed(!!urlParams.get('crc-proceed'));
            setHasPaid(!!urlParams.get('crc'));
        }
    }, []);

    return (
        <div className={cn(['tw-space-y-[16px]', 'tw-bg-[#E8F0FC] tw-p-[6px]'])}>
            <div className='tw-font-[500] tw-flex tw-items-center tw-gap-[8px] tw-text-[16px] tw-text-[#333]'>
                Complete a Criminal Record Check
                <CriminalCheckBadge className='tw-w-[185px]' />
            </div>
            <p>
                Add a criminal background check to your profile to show Taskers you're trustworthy, helping everyone
                transact with peace of mind.
            </p>
            {criminalRecordCheckStatus || hasPaid ? null : (
                <div>
                    <p>$24.95</p>
                    <p>Excluding taxes</p>
                </div>
            )}

            {criminalRecordCheckStatus || (hasPaid && (
                <InfoCallout>Payment received. Check email for next steps.</InfoCallout>
            )) || (
                <Button variant='contained' className='!tw-bg-[#00B451] hover:!tw-bg-[#00B451]' onClick={handlePayment}>
                    PAY
                </Button>
            )}
            <CriminalRecordCheckProceedModal
                user={props.user}
                open={openProceed}
                onClose={() => setOpenProceed(false)}
            />
            <CriminalRecordCheckSuccessModal />
        </div>
    );
};

function EmailNotSent({ user, setCriminalRecordCheck }) {
    const [isLoading, setIsLoading] = useState(false);

    const handleResend = async () => {
        setIsLoading(true);
        try {
            const result = await axios.post('/certn/resend-invite', { userId: user.ID, test: 'ting' });
            toastr.success(result.message);
            setCriminalRecordCheck(state => ({ ...state, Retries: 0, InviteFailed: false, InvitedAndPending: true }));
        } catch (err) {
            console.log('Cancel and refund error:', err);
            const errorMessage = err?.message || 'Please try again later.';
            toastr.error(errorMessage, 'Oops! Something went wrong.');
        }
        setIsLoading(false);
    };

    return (
        <div className='tw-space-y-[16px]'>
            <ErrorCallout>Error in sending email with next steps. Please click resend email button.</ErrorCallout>;
            <Button variant='outlined' onClick={handleResend} loading={isLoading}>
                RESEND EMAIL
            </Button>
            <div>
                <div className='tw-text-[16px] tw-font-[500]'>Didn't receive an email?</div>
                <p>
                    Please contact our{' '}
                    <a
                        href='https://task.marketplace.bcaa.com/pages/contact-us'
                        target='_blank'
                        className='tw-underline'
                    >
                        customer support team
                    </a>{' '}
                    and we'll help you out.
                </p>
            </div>
        </div>
    );
}

CriminalRecordCheck.propTypes = {
    user: PropTypes.shape({
        ID: PropTypes.string,
        CriminalRecordCheck: PropTypes.object
    }).isRequired
};

EmailNotSent.propTypes = CriminalRecordCheck.propTypes;
